import React, { useEffect, useState } from 'react';
import { Match, Team } from '@/types/cricket';
import { fetchLiveMatchRoles } from '@/services/supabaseService';
import { Trophy, Target, TrendingUp, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import BallByBallOverview from './BallByBallOverview';

interface PublicLiveScoreboardProps {
  match: Match;
  teams: Team[];
}

const cardClass =
  'rounded-xl shadow-lg border bg-gradient-to-br from-white via-blue-50 to-blue-100 p-4 transition-transform hover:scale-[1.02]';

const PublicLiveScoreboard: React.FC<PublicLiveScoreboardProps> = ({ match, teams }) => {
  const team1 = teams.find(t => t.id === match.team1Id);
  const team2 = teams.find(t => t.id === match.team2Id);
  const battingTeam = teams.find(t => t.id === match.currentBattingTeam);
  const bowlingTeam = teams.find(t => t.id !== match.currentBattingTeam);
  const score = match.currentBattingTeam === match.team1Id ? match.team1Score : match.team2Score;

  // Live roles state
  const [roles, setRoles] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!match?.id || !bowlingTeam?.id) return;
    setLoading(true);
    fetchLiveMatchRoles(match.id, bowlingTeam.id)
      .then(setRoles)
      .finally(() => setLoading(false));
  }, [match?.id, bowlingTeam?.id]);

  // Batsmen: striker and non-striker
  const batsmen = [roles?.striker, roles?.nonStriker].filter(Boolean).map((b: any) => ({
    id: b.id,
    name: b.name,
    runs: b.runs ?? '-',
    balls: b.balls ?? '-',
    fours: b.fours ?? '-',
    sixes: b.sixes ?? '-',
    isOnStrike: b.isOnStrike ?? false,
    sr: b.balls && b.balls > 0 && b.runs ? ((b.runs / b.balls) * 100).toFixed(1) : '-',
  }));

  // Bowler
  const bowler = roles?.bowler
    ? {
        name: roles.bowler.name,
        overs: roles.bowler.overs ?? '-',
        maidens: roles.bowler.maidens ?? '-',
        runs: roles.bowler.runs ?? '-',
        wickets: roles.bowler.wickets ?? '-',
        economy: roles.bowler.economy ?? '-',
      }
    : { name: '-', overs: '-', maidens: '-', runs: '-', wickets: '-', economy: '-' };

  // Partnership: sum runs/balls for the two batsmen
  const partnership = batsmen.length === 2 && batsmen[0].runs !== '-' && batsmen[1].runs !== '-'
    ? {
        runs: batsmen[0].runs + batsmen[1].runs,
        balls: batsmen[0].balls + batsmen[1].balls,
        batsmen: [batsmen[0].name, batsmen[1].name],
      }
    : { runs: 0, balls: 0, batsmen: ['-', '-'] };

  // Mock/fallback data for other details
  const currentRR = (score.overs + score.balls / (match.ballsPerOver || 6)) > 0
    ? (score.runs / (score.overs + score.balls / (match.ballsPerOver || 6))).toFixed(2)
    : '0.00';
  const requiredRR = '7.25';
  const target = 194;
  const lastWicket = 'Player C b Bowler Y 12 (15)';
  const extras = 8;
  const fallOfWickets = '1-23 (3.2), 2-67 (8.5), 3-89 (12.1)';
  const matchStage = '2nd Innings • Powerplay';

  return (
    <div className="space-y-6">
      {/* Match Info Card */}
      <div className="flex flex-col sm:flex-row justify-between items-center bg-gradient-to-r from-blue-100 via-white to-blue-50 p-4 rounded-xl shadow border">
        <div>
          <div className="flex items-center gap-2 text-lg font-bold">
            {team1?.logoUrl && <img src={team1.logoUrl} alt="" className="w-8 h-8 rounded-full" />}
            {team1?.name} vs {team2?.name}
            {team2?.logoUrl && <img src={team2.logoUrl} alt="" className="w-8 h-8 rounded-full" />}
          </div>
          <div className="text-sm text-gray-500">{match.city} • {new Date(match.matchDate).toLocaleDateString()}</div>
          <div className="text-xs text-blue-700 mt-1">{matchStage}</div>
        </div>
        <div className="mt-2 sm:mt-0">
          <motion.span
            animate={match.status === 'live' ? { opacity: [1, 0.5, 1] } : {}}
            transition={{ repeat: Infinity, duration: 1.2 }}
            className={`px-3 py-1 rounded-full text-xs font-semibold ${match.status === 'live' ? 'bg-green-100 text-green-700 shadow-green-200 shadow-md' : 'bg-gray-200 text-gray-600'}`}
          >
            {match.status?.toUpperCase()}
          </motion.span>
        </div>
      </div>

      {/* Score, Target, RR */}
      <motion.div
        key={score.runs + '-' + score.wickets}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        className="flex flex-col items-center bg-white p-6 rounded-xl shadow border"
      >
        <div className="text-3xl font-bold mb-1">
          {battingTeam?.name} <span className="text-blue-700">{score.runs}/{score.wickets}</span>
        </div>
        <div className="text-md text-gray-600 mb-2">{score.overs}.{score.balls} overs</div>
        <div className="flex flex-wrap gap-4 mt-2 justify-center">
          <div className="flex items-center gap-1 text-lg"><Target className="inline w-5 h-5" /> Target: <span className="font-bold">{target}</span></div>
          <div className="flex items-center gap-1 text-lg"><TrendingUp className="inline w-5 h-5" /> Required RR: <span className="font-bold">{requiredRR}</span></div>
          <div className="text-lg">Current RR: <span className="font-bold">{currentRR}</span></div>
        </div>
      </motion.div>

      {/* Batsmen, Bowler, Partnership */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div className={cardClass} whileHover={{ scale: 1.04 }}>
          <div className="font-semibold mb-1">Batsmen</div>
          {loading ? (
            <div className="text-gray-400">Loading...</div>
          ) : batsmen.length === 0 ? (
            <div className="text-gray-400">No batsmen</div>
          ) : batsmen.map(b => (
            <Tooltip.Root key={b.id} delayDuration={100}>
              <Tooltip.Trigger asChild>
                <div className="text-sm flex justify-between items-center gap-2 cursor-pointer">
                  <span>{b.name} {b.isOnStrike && <User className="inline w-4 h-4 text-green-600 animate-bounce" />}</span>
                  <span className="font-mono">{b.runs} ({b.balls}) <span className="text-xs text-gray-400">4s:{b.fours} 6s:{b.sixes}</span></span>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content side="top" className="bg-white border rounded shadow px-3 py-2 text-xs">
                <div><b>{b.name}</b></div>
                <div>Runs: {b.runs}</div>
                <div>Balls: {b.balls}</div>
                <div>4s: {b.fours} &nbsp; 6s: {b.sixes}</div>
                <div>Strike Rate: {b.sr}</div>
                <div>{b.isOnStrike ? 'On Strike' : 'Non-striker'}</div>
              </Tooltip.Content>
            </Tooltip.Root>
          ))}
        </motion.div>
        <motion.div className={cardClass} whileHover={{ scale: 1.04 }}>
          <div className="font-semibold mb-1">Partnership</div>
          <div className="text-lg">{partnership.runs} runs</div>
          <div className="text-xs text-gray-500">({partnership.balls} balls) {partnership.batsmen.join(' & ')}</div>
        </motion.div>
        <motion.div className={cardClass} whileHover={{ scale: 1.04 }}>
          <div className="font-semibold mb-1">Bowler</div>
          <Tooltip.Root delayDuration={100}>
            <Tooltip.Trigger asChild>
              <div className="text-sm flex justify-between items-center cursor-pointer">
                <span>{bowler.name}</span>
                <span className="font-mono">{bowler.overs}-{bowler.maidens}-{bowler.runs}-{bowler.wickets}</span>
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content side="top" className="bg-white border rounded shadow px-3 py-2 text-xs">
              <div><b>{bowler.name}</b></div>
              <div>Overs: {bowler.overs}</div>
              <div>Maidens: {bowler.maidens}</div>
              <div>Runs: {bowler.runs}</div>
              <div>Wickets: {bowler.wickets}</div>
              <div>Economy: {bowler.economy}</div>
            </Tooltip.Content>
          </Tooltip.Root>
          <div className="text-xs text-gray-500 mt-1">Econ: {bowler.economy}</div>
        </motion.div>
      </div>

      {/* Extras, Last Wicket, Fall of Wickets */}
      <div className="flex flex-wrap gap-4 justify-center mt-4">
        <motion.div
          className="bg-blue-50 px-4 py-2 rounded shadow text-sm"
          whileHover={{ scale: 1.08 }}
        >
          Extras: <span className="font-bold">{extras}</span>
        </motion.div>
        <AnimatePresence>
          <motion.div
            key={lastWicket}
            className="bg-red-50 px-4 py-2 rounded shadow text-sm"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            whileHover={{ scale: 1.08 }}
          >
            Last Wicket: <span className="font-bold">{lastWicket}</span>
          </motion.div>
        </AnimatePresence>
        <motion.div
          className="bg-yellow-50 px-4 py-2 rounded shadow text-sm"
          whileHover={{ scale: 1.08 }}
        >
          Fall of Wickets: <span className="font-bold">{fallOfWickets}</span>
        </motion.div>
      </div>

      {/* Ball by Ball Overview */}
      <BallByBallOverview match={match} teams={teams} />
    </div>
  );
};

export default PublicLiveScoreboard; 